import Header from "./components/header";
import Footer from "./components/footer";
import Loader from "./components/loader";
import { useEffect, useState } from "react";
import { useCMSContent } from "../hooks/useCMSContent";

const TermsCondition = () => {
  const [loading, setLoading] = useState(true);
  
  // CMS Content Hook - fetches content from API, falls back to hardcoded if unavailable
  const { cmsContent, getSectionContent, getSectionItems, hasCMSContent } = useCMSContent('terms-and-conditions');
  
  useEffect(() => {
    // SEO Meta Tags (will be overridden by CMS if available)
    if (!hasCMSContent) {
      document.title = "Terms and Conditions | TAK8 Car Rental";
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Read the terms and conditions for TAK8 Car Rental services in Perth. Understand your responsibilities, insurance options, and rental policies.');
      }
    }
    
    setTimeout(() => setLoading(false), 1000);
  }, [hasCMSContent]);

  // Helper to render list items from CMS or fallback
  const renderListItems = (sectionId, fallbackItems) => {
    const items = getSectionItems(sectionId);
    if (items && items.length > 0) {
      return items.map((item, index) => (
        <li key={item.id || index}>
          <img src="img/icon/dot.svg" alt="Check" />
          {item.bold && <b>{item.bold}</b>} {item.text || item.description}
        </li>
      ));
    }
    return fallbackItems;
  };

  return (
    <>
      {loading ? <Loader /> : ""}
      <Header />
      <section className="MainSection">
        <div className="container">
          <h1 className="Head_2">{getSectionContent('hero', 'title', 'TAK8 CAR RENTAL – TERMS AND CONDITIONS')}</h1>
        </div>
      </section>
      <section className="TermsSection">
        <div className="container">
          <div className="Text">
            <div 
              className="Pra_1 colorRed rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('intro', 'description', 'These terms and conditions are supplementary to, and does not override, any rights or obligations under the Competition and Consumer Act or similar State Laws. References to "the vehicle" refer to the rental car covered by this agreement.') 
              }}
            />
          </div>
          <div className="TextAlt">
            <h4 className="Head_3">{getSectionContent('responsibilities', 'title', '1. Responsibilities of the Renter')}</h4>
            <ul>
              {renderListItems('responsibilities', (
                <>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Return</b> The vehicle must be returned to the TAK8 depot/yard from where it was collected at the agreed time.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Substance-Free Driving</b> Driving under the influence of alcohol or drugs is prohibited.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>License Validity</b> The renter and any additional drivers must have valid driver's licenses.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Accountability</b> The renter is responsible for any breaches of these terms by additional drivers.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Authorized Drivers Only</b> No unauthorized individuals are allowed to operate the vehicle.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Road Conditions</b> The vehicle is to be driven only on paved roads.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Water Hazards</b> Avoid driving through water bodies such as rivers, creeks, or flooded roads.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>No Roof Items</b> Do not carry surfboards or other items on the roof of the vehicle.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Replacement Key Fee</b> If the rental car key is lost during your rental period, the renter will be responsible for covering the full replacement cost, including any associated fees.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Fuel</b> Return the vehicle with a full tank of unleaded petrol; shortages will be charged at $3.00 per litre.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Cleanliness</b> Return the vehicle in a clean condition, or a $75 cleaning fee applies.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>No Smoking or Pets</b> Smoking, vaping, or having pets in the vehicle incurs a $200 cleaning fee.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Traffic Infringements</b> A $25 administrative fee applies for each traffic infringement in addition to the fine.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Fair Use Policy</b> The vehicle is not to be used for security patrols or courier services.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Geographic Limits</b> Operate the vehicle within 500km of Perth; boundaries include Geraldton, Paynes Find, Southern Cross, Jerramungup, Albany.
                  </li>
                  <li>
                    <img src="img/icon/dot.svg" alt="Check" />
                    <b>Extended Use Area</b> A $150 fee applies to extend the area of use; extended boundaries include Exmouth, Carnarvon, Monkey Mia, Kalbarri, Meekatharra, Kalgoorlie, Norseman, Esperance.
                  </li>
                </>
              ))}
            </ul>
          </div>
          <div className="Text">
            <h4 className="Head_3">{getSectionContent('insurance', 'title', '2. Insurance Options and Damage Liability')}</h4>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('insurance', 'description', 'TAK8 offers two levels of coverage so you can choose the level of protection that suits your needs.') 
              }}
            />
            <h4 className="Head_7">{getSectionContent('standard-cover', 'title', 'STANDARD COVER – Collision Damage Responsibility')}</h4>
            <ul>
              <li>
                <img src="img/icon/dot.svg" alt="Check" />
                <b>Cost</b> $0.00 per day
              </li>
              <li>
                <img src="img/icon/dot.svg" alt="Check" />
                <b>Single Vehicle Excess</b> $5000 (Regardless of fault)
              </li>
            </ul>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('standard-cover', 'description', 'With this option, your maximum liability in the event of any accident or damage — including single vehicle damage — is $5000') 
              }}
            />
            <h4 className="Head_7">{getSectionContent('peace-of-mind', 'title', 'PEACE OF MIND COVER – Reduced Liability Protection')}</h4>
            <ul>
              <li>
                <img src="img/icon/dot.svg" alt="Check" />
                <b>Cost:</b> $22.00 per day
              </li>
              <li>
                <img src="img/icon/dot.svg" alt="Check" />
                <b>Single Vehicle Excess:</b> $500 (Regardless of fault)
              </li>
              <li>
                <img src="img/icon/dot.svg" alt="Check" />
                <b>Security Bond:</b> $200
              </li>
            </ul>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('peace-of-mind', 'description', 'The maximum liability is reduced to $500 under the PEACE OF MIND COVER. For single vehicle incidents this also includes, but is not limited to, the following minor damages') 
              }}
            />
            <ul>
              <li><img src="img/icon/dot.svg" alt="Check" />Scratches to the vehicle's paintwork</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Minor dents or dings on body panels</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Curb rash or scuff marks on alloy wheels</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Cracked or chipped side mirrors</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Cracked or chipped headlights or taillights</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Bent or cracked number plates</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Damage to front or rear bumpers from low-speed impacts</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Broken or missing wiper blades</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Damage to plastic trim or mouldings</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Cracks or chips in the windshield (caused without collision)</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Door edge dents from opening into walls or poles</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Damage from reversing into low objects (e.g., bollards, fences)</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Underbody scrapes from driveways or uneven roads</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Minor scuffs on side skirts or bumpers from tight parking</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Punctured or flat tires due to curb or pothole impact</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Misalignment or damage to sensors</li>
            </ul>
            <p className="Pra_1 py-2">
              Important: Your liability is strictly limited to $500 for single vehicle damage only when the incident is accidental, and you have complied with all terms of this agreement. If the damage is caused by negligence, a breach of this agreement, or reckless driving, you are fully liable for all resulting repair costs. The $500 cap does not apply in such cases.
            </p>
            <h4 className="Head_7">{getSectionContent('single-vehicle-excess', 'title', "What is 'Single Vehicle Excess'?")}</h4>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('single-vehicle-excess', 'description', 'Single Vehicle Excess is the maximum amount you must pay if the rental vehicle is damaged in an incident where no other vehicle is involved. This includes but is not limited to') 
              }}
            />
            <ul>
              <li><img src="img/icon/dot.svg" alt="Check" />Collisions with stationary objects (e.g., poles, barriers, trees)</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Damage from scraping curbs or reversing into walls</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Driving off-road or into potholes causing suspension or underbody damage</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Any damage where no third-party vehicle is part of the accident</li>
            </ul>
            <p className="Pra_1 py-2">All renters are liable for Single Vehicle Damage</p>
            <ul>
              <li><img src="img/icon/dot.svg" alt="Check" />Under STANDARD COVER, the excess is $5000</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Under PEACE OF MIND COVER, the excess is reduced to $500</li>
            </ul>
            <p className="Pra_1 py-2">Even with PEACE OF MIND COVER, this type of damage is not waived to zero.</p>
            <h4 className="Head_7">{getSectionContent('excluded-damages', 'title', 'Excluded Damages (Not Covered by Any Waiver)')}</h4>
            <ul>
              <li><img src="img/icon/dot.svg" alt="Check" />Failure to immediately report an accident or incident (within 6–12 hours at most) to TAK8, or fleeing the scene. In such cases, the renter is fully liable for all repair and recovery costs and will be charged at minimum the full standard excess of $5,000, regardless of the cover selected.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Intentional or reckless damage, including driving under the influence of drugs or alcohol.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Unauthorized use, including driving outside the approved geographical area or by an unauthorized driver.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Interior damage such as stains, rips, burns, or damage from negligence.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Mechanical damage caused by ignoring dashboard warnings or continued use of the vehicle after fault indicators appear.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Towing the vehicle without prior authorization from TAK8.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Leaving the vehicle unlocked or leaving the keys inside the vehicle.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Underbody or water damage, regardless of cause and whether another vehicle was involved.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Theft or loss resulting from failure to return the keys or secure the vehicle.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Damage incurred during a breach of this agreement.</li>
            </ul>
          </div>
          <div className="TextAlt">
            <h4 className="Head_3">{getSectionContent('inspections', 'title', '3. Pre-Rental and Post-Rental Inspections')}</h4>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('inspections', 'description', "All vehicles are inspected before and after each rental. Renters are encouraged to review the vehicle's condition with a TAK8 representative at pick-up and note any existing damage to avoid disputes upon return.") 
              }}
            />
          </div>
          <div className="Text">
            <h4 className="Head_3">{getSectionContent('business-hours', 'title', '4. Business Hours')}</h4>
            <p className="Pra_1 py-2">
              <b>Monday to Friday:</b> 9:00 AM to 5:00 PM<br />
              <b>Saturday & Sunday:</b> Available by booking between 9:00 AM to 5:00 PM
            </p>
          </div>
          <div className="Text">
            <h4 className="Head_3">{getSectionContent('non-return', 'title', 'Non-Return, Non-Payment & Rental Extensions')}</h4>
            <ul>
              <li><img src="img/icon/dot.svg" alt="Check" />If the vehicle is not returned on the agreed date and/or rental payment is not made in full, all insurance cover (including PEACE OF MIND COVER) becomes void. The renter is then fully liable for the full standard excess of $5,000 and any associated recovery or repair costs.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Extensions requested by phone require the renter to bring the vehicle to the TAK8 depot for inspection and provide clear photos of the vehicle's current condition before the rental period and insurance cover can be extended.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Insurance cover will only apply if the rental account is fully up to date.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />Payments made in arrears to bring the account current do not provide retrospective insurance cover for the overdue period.</li>
              <li><img src="img/icon/dot.svg" alt="Check" />In the event of non-return, non-payment, failure to report an accident within the required timeframe, or any other breach resulting in liability, TAK8 reserves the right to charge the renter's nominated payment method provided at the time of signing the contract for the applicable excess, repair costs, or outstanding rental fees.</li>
            </ul>
          </div>
          <div className="TextAlt">
            <h4 className="Head_3">{getSectionContent('tracking', 'title', '5. Vehicle Tracking and Recovery')}</h4>
            <div 
              className="Pra_1 py-2 rich-content"
              dangerouslySetInnerHTML={{ 
                __html: getSectionContent('tracking', 'description', 'TAK8 vehicles may be equipped with tracking devices for security and recovery purposes. If a vehicle is not returned on time or is believed to be stolen, TAK8 reserves the right to track, disable, and recover the vehicle. Personal details may be shared with authorities in the event of theft or a serious breach of this agreement.') 
              }}
            />
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default TermsCondition;
